import { action, computed, makeObservable, observable } from 'mobx'

import { IGeoJson2DGeographicCoordinates } from '~/client/graph'

interface IPendingPlacementResult {
  id: string
  coord: IGeoJson2DGeographicCoordinates
}

export default class PendingPlacement {
  @observable.ref public queue: string[] = []
  @observable.ref public coord: IGeoJson2DGeographicCoordinates = null
  @observable public isConfirming: boolean = false
  @observable public total: number = 0

  public constructor() {
    makeObservable(this)
  }

  @computed
  public get id(): string {
    return this.queue[0] ?? null
  }

  @computed
  public get remaining(): number {
    return this.queue.length
  }

  @computed
  public get currentIndex(): number {
    return this.total - this.remaining + 1
  }

  @action.bound
  public begin(
    ids: string[],
    seedCoord: IGeoJson2DGeographicCoordinates,
  ): void {
    if (this.isConfirming || ids.length === 0) {
      return
    }
    this.queue = [...ids]
    this.total = ids.length
    this.coord = { ...seedCoord }
    this.isConfirming = false
  }

  @action.bound
  public enqueue(ids: string[]): void {
    if (!this.id) {
      return
    }
    const existing = new Set(this.queue)
    const appended = ids.filter(id => !!id && !existing.has(id))
    if (appended.length === 0) {
      return
    }
    this.queue = [...this.queue, ...appended]
    this.total += appended.length
  }

  @action.bound
  public updateCoord(coord: IGeoJson2DGeographicCoordinates): void {
    if (!this.id || this.isConfirming) {
      return
    }
    this.coord = { ...coord }
  }

  @action.bound
  public markConfirming(): IPendingPlacementResult | null {
    if (!this.id || !this.coord || this.isConfirming) {
      return null
    }
    this.isConfirming = true
    return {
      id: this.id,
      coord: this.coord,
    }
  }

  @action.bound
  public advance(seedCoord: IGeoJson2DGeographicCoordinates): void {
    this.queue = this.queue.slice(1)
    this.isConfirming = false
    this.coord = this.queue.length > 0 ? { ...seedCoord } : null
  }

  @action.bound
  public clearConfirming(): void {
    this.isConfirming = false
  }

  @action.bound
  public cancel(): void {
    this.queue = []
    this.coord = null
    this.isConfirming = false
    this.total = 0
  }
}
